/**
 * Écran d'administration « Tarifs IA » (ADMIN_USERS, contrat §9).
 *
 * - Liste des tarifs (`ai_model_price`, en insertion seule) et des modèles vus
 *   dans les appels sans tarif applicable (« non valorisables »).
 * - Ajout seulement : aucune modification ni suppression. On corrige un tarif
 *   en ajoutant une ligne à une date d'effet postérieure.
 * - Prix saisis et transmis en CHAÎNES décimales (jamais convertis en
 *   flottant) ; devise obligatoire, sans valeur par défaut.
 */
import React, { useRef, useState } from 'react';
import { Alert, App, Button, Card, Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import AppLayout from '../../components/layout/AppLayout';
import {
    AI_PRICE_FIELDS,
    API_KINDS,
    type AiModelPrice,
    type AiPriceInput,
    type AiPricesResponse,
    type ApiKind,
    type UnpricedModel,
} from '../../api/monitoring/v2';
import { cardStyle, INK } from './monitoringTheme';
import QueryStatusView from './shared/QueryStatusView';
import { useMonitoringQuery } from './shared/useMonitoringQuery';
import { useMonitoringApiRef } from './shared/useScopes';
import { useEnumLabel } from './shared/i18nCatalog';
import { useFormat } from './shared/format';
import CompactValue from './shared/CompactValue';

const { Text, Title } = Typography;

export const PRICE_FIELDS = AI_PRICE_FIELDS;
type PriceField = (typeof PRICE_FIELDS)[number];

/** Champs de prix obligatoires par type d'API (contrainte CHECK côté base pour `chat`). */
export const REQUIRED_PRICES: Record<ApiKind, readonly PriceField[]> = {
    chat: ['input_per_1m', 'cached_input_per_1m', 'output_per_1m'],
    embedding: ['input_per_1m'],
    stt: [],
    tts: [],
    ocr: ['per_1k_pages'],
};

/**
 * Forme du code fournisseur imposée par la table (CHECK
 * ai_model_price_provider_check, migration 0132) : contrôle de structure.
 */
export const PROVIDER_RE = /^[a-z0-9][a-z0-9_-]*$/;

export const DECIMAL_RE = /^\d+(\.\d+)?$/;
export const CURRENCY_RE = /^[A-Z]{3}$/;

type FormValues = {
    provider: string;
    model: string;
    api_kind: ApiKind;
    effective_from: Dayjs;
    currency: string;
} & Partial<Record<PriceField, string>>;

/** Corps du POST : les prix restent des chaînes ; un champ vide vaut null. */
export const toPriceInput = (values: FormValues): AiPriceInput => {
    const price = (field: PriceField): string | null => {
        const raw = values[field]?.trim();
        return raw ? raw : null;
    };
    return {
        provider: values.provider.trim(),
        model: values.model.trim(),
        api_kind: values.api_kind,
        effective_from: values.effective_from.format('YYYY-MM-DD'),
        input_per_1m: price('input_per_1m'),
        cached_input_per_1m: price('cached_input_per_1m'),
        output_per_1m: price('output_per_1m'),
        reasoning_per_1m: price('reasoning_per_1m'),
        per_audio_minute: price('per_audio_minute'),
        per_1m_characters: price('per_1m_characters'),
        cache_write_per_1m: price('cache_write_per_1m'),
        text_output_per_1m: price('text_output_per_1m'),
        per_1k_pages: price('per_1k_pages'),
        currency: values.currency.trim(),
    };
};

/**
 * Message d'un refus du POST (codes du lot 8) : 409 `price_exists`, 400
 * `invalid_price` (avec le détail du serveur), 415 type de contenu refusé ;
 * tout autre échec reste un échec générique, jamais un succès silencieux.
 */
export const describePriceError = (err: unknown, t: (key: string, values?: Record<string, unknown>) => string): string => {
    const response = (err as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response;
    const code = response?.data?.error;
    if (response?.status === 409 || code === 'price_exists') return t('monitoring.aiPrices.conflict');
    if (response?.status === 400 && code === 'invalid_price') {
        return t('monitoring.aiPrices.invalidPrice', { detail: response.data?.message ?? '' });
    }
    if (response?.status === 415) return t('monitoring.aiPrices.unsupportedMediaType');
    return t('monitoring.aiPrices.saveError');
};

const MonitoringAiPrices: React.FC = () => {
    const { t } = useTranslation();
    const f = useFormat();
    const enumLabel = useEnumLabel();
    const { message } = App.useApp();
    const apiRef = useMonitoringApiRef();
    const [form] = Form.useForm<FormValues>();
    const [saving, setSaving] = useState(false);
    const [conflict, setConflict] = useState<string | null>(null);
    const formCardRef = useRef<HTMLDivElement>(null);

    const query = useMonitoringQuery<AiPricesResponse>('ai-prices', (signal) => apiRef.current.getAiPrices(signal));
    const apiKind = Form.useWatch('api_kind', form);

    const title = t('monitoring.aiPrices.title');

    if (query.status === 'role_required') {
        return (
            <AppLayout title={title}>
                <Alert type="error" showIcon data-testid="role-required" message={t('monitoring.errors.roleRequired')} />
            </AppLayout>
        );
    }

    const submit = async (values: FormValues) => {
        setSaving(true);
        setConflict(null);
        try {
            await apiRef.current.createAiPrice(toPriceInput(values));
            message.success(t('monitoring.aiPrices.saved'));
            form.resetFields();
            query.reload();
        } catch (err) {
            setConflict(describePriceError(err, t));
        } finally {
            setSaving(false);
        }
    };

    const prefill = (row: UnpricedModel) => {
        form.setFieldsValue({ provider: row.provider, model: row.model, api_kind: row.api_kind });
        formCardRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    };

    const priceCell = (value: string | null, currency: string) =>
        value === null ? <Text style={{ color: INK.muted }}>—</Text> : f.money(value, currency, Math.max(2, value.split('.')[1]?.length ?? 0));

    return (
        <AppLayout title={title}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert type="info" showIcon message={t('monitoring.aiPrices.insertOnly')} />
                <QueryStatusView query={query} tiles={0} />

                {query.data && (
                    <Card style={cardStyle} title={<Title level={5} style={{ margin: 0 }}>{t('monitoring.aiPrices.unpriced')}</Title>}>
                        <Table
                            size="small"
                            rowKey={(r) => `${r.provider}|${r.model}|${r.api_kind}`}
                            pagination={false}
                            dataSource={query.data.unpriced_models}
                            locale={{ emptyText: t('monitoring.aiPrices.noUnpriced') }}
                            columns={[
                                { key: 'provider', dataIndex: 'provider', title: t('monitoring.aiPrices.provider') },
                                { key: 'model', dataIndex: 'model', title: t('monitoring.col.model') },
                                {
                                    key: 'kind',
                                    dataIndex: 'api_kind',
                                    title: t('monitoring.aiPrices.apiKind'),
                                    render: (v: string) => <Tag bordered={false}>{enumLabel('api_kind', v)}</Tag>,
                                },
                                { key: 'first', dataIndex: 'first_seen', title: t('monitoring.aiPrices.firstSeen'), render: (v: string) => f.dateTime(v) },
                                { key: 'last', dataIndex: 'last_seen', title: t('monitoring.aiPrices.lastSeen'), render: (v: string) => f.dateTime(v) },
                                { key: 'calls', dataIndex: 'calls', title: t('monitoring.col.calls'), align: 'end', render: (n: number) => <CompactValue value={n} /> },
                                {
                                    key: 'action',
                                    render: (_: unknown, row: UnpricedModel) => (
                                        <Button size="small" onClick={() => prefill(row)}>
                                            {t('monitoring.aiPrices.priceIt')}
                                        </Button>
                                    ),
                                },
                            ]}
                        />
                    </Card>
                )}

                {query.data && (
                    <Card style={cardStyle} title={<Title level={5} style={{ margin: 0 }}>{t('monitoring.aiPrices.prices')}</Title>}>
                        <Table<AiModelPrice>
                            size="small"
                            rowKey={(r) => `${r.provider}|${r.model}|${r.api_kind}|${r.effective_from}`}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            dataSource={query.data.prices}
                            columns={[
                                { key: 'provider', dataIndex: 'provider', title: t('monitoring.aiPrices.provider') },
                                { key: 'model', dataIndex: 'model', title: t('monitoring.col.model') },
                                {
                                    key: 'kind',
                                    dataIndex: 'api_kind',
                                    title: t('monitoring.aiPrices.apiKind'),
                                    render: (v: string) => <Tag bordered={false}>{enumLabel('api_kind', v)}</Tag>,
                                },
                                {
                                    key: 'from',
                                    dataIndex: 'effective_from',
                                    title: t('monitoring.aiPrices.effectiveFrom'),
                                    render: (v: string) => f.dateTime(v),
                                },
                                ...PRICE_FIELDS.map((field) => ({
                                    key: field,
                                    title: t(`monitoring.aiPrices.field.${field}`),
                                    align: 'end' as const,
                                    render: (_: unknown, row: AiModelPrice) => priceCell(row[field], row.currency),
                                })),
                                { key: 'source', dataIndex: 'source', title: t('monitoring.aiPrices.source') },
                                {
                                    key: 'created',
                                    dataIndex: 'created_at',
                                    title: t('monitoring.aiPrices.createdAt'),
                                    render: (v: string) => f.dateTime(v),
                                },
                            ]}
                        />
                    </Card>
                )}

                <div ref={formCardRef}>
                    <Card style={cardStyle} title={<Title level={5} style={{ margin: 0 }}>{t('monitoring.aiPrices.add')}</Title>}>
                        {conflict && <Alert type="error" showIcon data-testid="price-error" message={conflict} style={{ marginBottom: 12 }} />}
                        <Form<FormValues> form={form} layout="vertical" onFinish={submit} requiredMark>
                            <Row gutter={12}>
                                <Col xs={12} md={4}>
                                    <Form.Item
                                        name="provider"
                                        label={t('monitoring.aiPrices.provider')}
                                        normalize={(v: string) => (v ?? '').toLowerCase()}
                                        rules={[
                                            { required: true, whitespace: true, message: t('monitoring.aiPrices.required') },
                                            { pattern: PROVIDER_RE, message: t('monitoring.aiPrices.providerInvalid') },
                                        ]}
                                    >
                                        <Input dir="ltr" placeholder="openai" />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Item
                                        name="model"
                                        label={t('monitoring.col.model')}
                                        rules={[{ required: true, whitespace: true, message: t('monitoring.aiPrices.required') }]}
                                    >
                                        <Input dir="ltr" />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={5}>
                                    <Form.Item
                                        name="api_kind"
                                        label={t('monitoring.aiPrices.apiKind')}
                                        rules={[{ required: true, message: t('monitoring.aiPrices.required') }]}
                                    >
                                        <Select options={API_KINDS.map((k) => ({ value: k, label: enumLabel('api_kind', k) }))} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={5}>
                                    <Form.Item
                                        name="effective_from"
                                        label={t('monitoring.aiPrices.effectiveFrom')}
                                        rules={[{ required: true, message: t('monitoring.aiPrices.required') }]}
                                    >
                                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Item
                                        name="currency"
                                        label={t('monitoring.aiPrices.currency')}
                                        normalize={(v: string) => (v ?? '').toUpperCase()}
                                        rules={[
                                            { required: true, message: t('monitoring.aiPrices.required') },
                                            { pattern: CURRENCY_RE, message: t('monitoring.aiPrices.currencyInvalid') },
                                        ]}
                                    >
                                        <Input dir="ltr" maxLength={3} placeholder="ISO 4217" />
                                    </Form.Item>
                                </Col>
                                {PRICE_FIELDS.map((field) => (
                                    <Col key={field} xs={12} md={8} lg={4}>
                                        <Form.Item
                                            name={field}
                                            label={t(`monitoring.aiPrices.field.${field}`)}
                                            rules={[
                                                {
                                                    required: !!apiKind && REQUIRED_PRICES[apiKind as ApiKind]?.includes(field),
                                                    message: t('monitoring.aiPrices.required'),
                                                },
                                                { pattern: DECIMAL_RE, message: t('monitoring.aiPrices.decimalInvalid') },
                                            ]}
                                        >
                                            <Input dir="ltr" inputMode="decimal" placeholder="0.00" />
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>
                            <Text style={{ display: 'block', fontSize: 12, color: INK.muted, marginBottom: 12 }}>
                                {t('monitoring.aiPrices.reasoningHelp')}
                            </Text>
                            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={saving}>
                                {t('monitoring.aiPrices.submit')}
                            </Button>
                        </Form>
                    </Card>
                </div>
            </Space>
        </AppLayout>
    );
};

export default MonitoringAiPrices;
